/**
 * Contact Service for LC Performance (Supabase)
 * 
 * Direct Supabase integration for contact form submissions
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';

/**
 * Submit a contact form
 * @param {Object} contactData - Contact form data
 * @param {string} contactData.name - Full name (required)
 * @param {string} contactData.email - Email address (required)
 * @param {string} contactData.phone - Phone number (optional)
 * @param {string} contactData.subject - Subject/program interest (optional)
 * @param {string} contactData.message - Message (required)
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function submitContact(contactData) {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured' };
  }

  if (!contactData.name || !contactData.email || !contactData.message) {
    return { success: false, error: 'Name, email, and message are required' };
  }

  try {
    // Get client info for tracking
    const ipAddress = null; // Would be set server-side
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;

    const { data, error } = await supabase
      .from('contacts')
      .insert({
        name: contactData.name.trim(),
        email: contactData.email.trim().toLowerCase(),
        phone: contactData.phone?.trim() || null,
        subject: contactData.subject || contactData.program || null,
        message: contactData.message.trim(),
        status: 'new',
        source: 'contact_form',
        ip_address: ipAddress,
        user_agent: userAgent,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error submitting contact form:', error);
    return {
      success: false,
      error: error.message || 'Failed to submit contact form',
    };
  }
}

/**
 * Get all contacts (admin only - public function for use in adminService)
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Filter by status
 * @param {number} filters.limit - Number of results
 * @param {number} filters.offset - Pagination offset
 * @returns {Promise<{success: boolean, data?: Array, error?: string, count?: number}>}
 */
export async function getAllContacts(filters = {}) {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured', data: [] };
  }

  try {
    const {
      status,
      limit = 50,
      offset = 0,
      sortBy = 'created_at',
      order = 'desc',
    } = filters;

    // Build query
    let query = supabase
      .from('contacts')
      .select('*', { count: 'exact' });

    // Apply filters
    if (status) {
      query = query.eq('status', status);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: order === 'asc' });

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      success: true,
      data: data || [],
      count: count || 0,
    };
  } catch (error) {
    console.error('Error fetching contacts:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch contacts',
      data: [],
      count: 0,
    };
  }
}
